import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sequencesApi, contactsApi } from "@/api";
import type { SequenceEnrollment, EnrollmentStatus } from "@/types";
import { SEQUENCE_TRIGGER_LABELS, SEQUENCE_STATUS_LABELS, ENROLLMENT_STATUS_LABELS } from "@/types";
import { format } from "date-fns";
import {
  ArrowLeft, Play, Pause, Edit, UserPlus, LogOut,
  Mail, Clock, CheckCircle, XCircle,
} from "lucide-react";

const enrollmentStatusColors: Record<EnrollmentStatus, string> = {
  ACTIVE: "bg-blue-100 text-blue-700",
  PAUSED: "bg-slate-100 text-slate-700",
  COMPLETED: "bg-green-100 text-green-700",
  EXITED: "bg-red-100 text-red-700",
};

export default function SequenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [enrollPage, setEnrollPage] = useState(1);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollSearch, setEnrollSearch] = useState("");

  const { data: sequence, isLoading } = useQuery({
    queryKey: ["sequence", id],
    queryFn: () => sequencesApi.get(id!),
    enabled: !!id,
  });

  const { data: enrollments } = useQuery({
    queryKey: ["sequence-enrollments", id, enrollPage],
    queryFn: () => sequencesApi.enrollments(id!, { page: String(enrollPage), limit: "20" }),
    enabled: !!id,
  });

  const { data: searchResults } = useQuery({
    queryKey: ["contacts-search", enrollSearch],
    queryFn: () => contactsApi.list({ search: enrollSearch, limit: "10", status: "ACTIVE" }),
    enabled: enrollSearch.length >= 2,
  });

  const activateMutation = useMutation({
    mutationFn: () => sequencesApi.activate(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sequence", id] }),
  });

  const pauseMutation = useMutation({
    mutationFn: () => sequencesApi.pause(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sequence", id] }),
  });

  const enrollMutation = useMutation({
    mutationFn: (contactIds: string[]) => sequencesApi.enroll(id!, contactIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence", id] });
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollments", id] });
      setShowEnrollModal(false);
      setEnrollSearch("");
    },
  });

  const exitMutation = useMutation({
    mutationFn: (enrollmentId: string) => sequencesApi.exitEnrollment(id!, enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollments", id] });
      queryClient.invalidateQueries({ queryKey: ["sequence", id] });
    },
  });

  if (isLoading || !sequence) {
    return <p className="text-slate-500">読み込み中...</p>;
  }

  const stats = sequence.enrollmentStats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/sequences")} className="p-1 hover:bg-slate-100 rounded">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-xl font-bold">{sequence.name}</h2>
            {sequence.description && (
              <p className="text-sm text-slate-500 mt-0.5">{sequence.description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {sequence.status === "ACTIVE" ? (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
            >
              <Pause size={16} /> 停止
            </button>
          ) : (
            <button
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              <Play size={16} /> 有効化
            </button>
          )}
          <button
            onClick={() => setShowEnrollModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <UserPlus size={16} /> 手動登録
          </button>
          <button
            onClick={() => navigate(`/sequences/${id}/edit`)}
            className="flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
          >
            <Edit size={16} /> 編集
          </button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <span className="text-xs text-slate-500">ステータス</span>
          <p className="text-lg font-bold mt-1">{SEQUENCE_STATUS_LABELS[sequence.status]}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <span className="text-xs text-slate-500">トリガー</span>
          <p className="text-sm font-medium mt-1">{SEQUENCE_TRIGGER_LABELS[sequence.triggerType]}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center gap-1">
            <Clock size={12} className="text-blue-500" />
            <span className="text-xs text-slate-500">進行中</span>
          </div>
          <p className="text-2xl font-bold text-blue-600 mt-1">{stats.ACTIVE || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center gap-1">
            <CheckCircle size={12} className="text-green-500" />
            <span className="text-xs text-slate-500">完了</span>
          </div>
          <p className="text-2xl font-bold text-green-600 mt-1">{stats.COMPLETED || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center gap-1">
            <XCircle size={12} className="text-red-500" />
            <span className="text-xs text-slate-500">退出</span>
          </div>
          <p className="text-2xl font-bold text-red-600 mt-1">{stats.EXITED || 0}</p>
        </div>
      </div>

      {/* Step flow */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="font-bold text-sm text-slate-700 mb-4">ステップフロー</h3>
        <div className="space-y-3">
          {sequence.steps?.map((step, idx) => (
            <div key={step.id} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                  {step.stepNumber}
                </div>
                {idx < (sequence.steps?.length || 0) - 1 && (
                  <div className="w-px h-8 bg-slate-300 mt-1" />
                )}
              </div>
              <div className="flex-1 border rounded-lg p-3 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-slate-400" />
                  <span className="text-sm font-medium">
                    {step.emailTemplate?.name || "テンプレート"}
                  </span>
                  <span className="text-xs text-slate-400">
                    ({step.delayDays}日後)
                  </span>
                </div>
                {step.subject && (
                  <p className="text-xs text-slate-500 mt-1">件名: {step.subject}</p>
                )}
                {step.condition && (step.condition as any).branch !== "none" && (
                  <div className="text-xs text-blue-600 mt-1">
                    分岐: 前ステップ{(step.condition as any).branch === "opened" ? "開封" : "クリック"}
                    {(step.condition as any).ifTrue && ` → Yes: Step ${(step.condition as any).ifTrue}`}
                    {(step.condition as any).ifFalse && ` / No: Step ${(step.condition as any).ifFalse}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Enrollments */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <h3 className="text-sm font-bold">登録者 ({enrollments?.total || 0})</h3>
        </div>
        {enrollments && enrollments.data.length > 0 ? (
          <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">コンタクト</th>
                  <th className="text-center px-4 py-2 font-medium text-slate-600">ステップ</th>
                  <th className="text-center px-4 py-2 font-medium text-slate-600">ステータス</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">次回実行</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {enrollments.data.map((e: SequenceEnrollment) => (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-medium">{e.contact?.email}</div>
                      <div className="text-xs text-slate-400">
                        {[e.contact?.lastName, e.contact?.firstName].filter(Boolean).join("")}
                        {e.contact?.company && ` (${e.contact.company})`}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">{e.currentStep}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${enrollmentStatusColors[e.status]}`}>
                        {ENROLLMENT_STATUS_LABELS[e.status]}
                      </span>
                      {e.exitReason && (
                        <span className="text-xs text-slate-400 ml-1">({e.exitReason})</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-500 text-xs">
                      {e.status === "ACTIVE" ? format(new Date(e.nextStepAt), "MM/dd HH:mm") : "-"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {e.status === "ACTIVE" && (
                        <button
                          onClick={() => exitMutation.mutate(e.id)}
                          disabled={exitMutation.isPending}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          <LogOut size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {enrollments.totalPages > 1 && (
              <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  {enrollments.total}件中 {(enrollPage - 1) * 20 + 1}-{Math.min(enrollPage * 20, enrollments.total)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEnrollPage(Math.max(1, enrollPage - 1))}
                    disabled={enrollPage === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                  >
                    前へ
                  </button>
                  <button
                    onClick={() => setEnrollPage(Math.min(enrollments.totalPages, enrollPage + 1))}
                    disabled={enrollPage >= enrollments.totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                  >
                    次へ
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-slate-400 text-sm">登録者がいません</div>
        )}
      </div>

      {/* Enroll modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-bold">コンタクトを手動登録</h3>
            <input
              value={enrollSearch}
              onChange={(e) => setEnrollSearch(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="メール、会社名、名前で検索..."
              autoFocus
            />
            {searchResults && searchResults.data.length > 0 && (
              <div className="max-h-60 overflow-y-auto border rounded divide-y">
                {searchResults.data.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => enrollMutation.mutate([c.id])}
                    disabled={enrollMutation.isPending}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                  >
                    <div className="font-medium">{c.email}</div>
                    <div className="text-xs text-slate-400">
                      {[c.lastName, c.firstName].filter(Boolean).join("")}
                      {c.company && ` (${c.company})`}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {enrollMutation.isSuccess && (
              <p className="text-sm text-green-600">登録しました</p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => { setShowEnrollModal(false); setEnrollSearch(""); }}
                className="px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
